import { Typography } from "@mui/material";
import { memo, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Icon, NavLink } from "../../../common/components";
import { useAuth } from "../../../common/hooks/useAuth";
import { AppToolbarContext } from "../../../context/AppToolbar";
import { Styles, palette, spacing } from "../../../styles/theme";
import { ROUTES } from "../../../constant/common.constant";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { isFrench, setEnglish, setFrench } from "../../../utils/LocaleUtil";
import { isProd } from "../../../utils/env";
import RageQuitModal from "../../../components/RageQuitModal";

const styles: Styles = {
  backItem: {
    display: "flex",
    alignItems: "center",
    px: spacing.wrapperMobile,
    py: 1.5,
    mb: spacing.wrapperMobile,
    gap: 0.5,
    cursor: "pointer",
    "&:active": {
      background: palette.brand.paleEffect,
    },
  },
  list: {
    px: spacing.wrapperMobile,
    gap: spacing.regularMobile,
    display: "flex",
    flexDirection: "column",
  },
};

interface Props {
  onBackClick: () => void;
}

const UserProfileDrawerContent = ({ onBackClick }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toggleNavigationDrawerOpen } = useContext(AppToolbarContext);
  const { setUSD, setCAD, isCAD, isUSD } = useCurrency();
  const { logout, userInfo } = useAuth();
  const [isRageQuitModalOpen, setIsRageQuitModalOpen] = useState(false);
  // eslint-disable-next-line
  const isFr = useMemo(() => isFrench(), [i18n.language]);

  const onClick = (callback?: () => void) => () => {
    callback?.();
    onBackClick();
    toggleNavigationDrawerOpen?.();
  };

  return (
    <Box>
      <Box sx={styles.backItem} onClick={onBackClick}>
        <Icon icon="chevron_left" size={24} color={palette.grey.nearBlack} />
        <Typography variant="textRegularBold">
          {t("navigation.back_to_menu")}
        </Typography>
      </Box>
      <Box sx={styles.list}>
        <Box>
          <Typography variant="h2" mb={spacing.mediumMobile}>
            {t("profile.title")}
          </Typography>
          <NavLink
            content={t("profile.my_profile")}
            to={ROUTES.USER_PROFILE}
            onClick={onClick(() => navigate(ROUTES.USER_PROFILE))}
          />
          <NavLink
            content={t("profile.all_orders")}
            to={ROUTES.ADMIN_ALL_ORDERS}
            adminOnly
            onClick={onClick(() => navigate(ROUTES.ADMIN_ALL_ORDERS))}
          />
          <NavLink
            content={t("profile.manage_distributors")}
            to={ROUTES.ADMIN_DISTRIBUTORS}
            adminOnly
            onClick={onClick(() => navigate(ROUTES.ADMIN_DISTRIBUTORS))}
          />
          {userInfo.isAdmin && (
            <NavLink
              content="Manage user roles"
              to={ROUTES.ADMIN_USERS}
              adminOnly
              onClick={onClick(() => navigate(ROUTES.ADMIN_USERS))}
            />
          )}

          {!isProd && (
            <>
              <NavLink
                content={t("profile.specs_generator")}
                to={ROUTES.SPECS_GENERATOR}
                adminOnly
                onClick={onClick(() => navigate(ROUTES.SPECS_GENERATOR))}
              />
              <NavLink
                content={"Families"}
                to={ROUTES.SPECS_FAMILIES}
                adminOnly
                onClick={onClick(() => navigate(ROUTES.SPECS_FAMILIES))}
              />
              <NavLink
                content={"Fabrics"}
                to={ROUTES.SPECS_FABRICS}
                adminOnly
                onClick={onClick(() => navigate(ROUTES.SPECS_FABRICS))}
              />
              <NavLink
                content={"Decorations"}
                to={ROUTES.SPECS_DECORATIONS}
                adminOnly
                onClick={onClick(() => navigate(ROUTES.SPECS_DECORATIONS))}
              />
            </>
          )}
          <NavLink
            content={t("profile.rage_quit")}
            adminOnly
            onClick={() => setIsRageQuitModalOpen(true)}
          />

          <NavLink
            content={t("profile.my_orders")}
            to={ROUTES.ORDERS}
            onClick={onClick(() => navigate(ROUTES.ORDERS))}
          />
          <NavLink
            content={t("general.logout")}
            data-test="mobileLeftNavigation.userProfileNavLink.logout"
            to={""}
            onClick={(e) => {
              e.preventDefault();
              logout();
            }}
          />
        </Box>
        <Box>
          <Typography variant="h2" mb={spacing.mediumMobile}>
            {t("profile.language")}
          </Typography>
          <NavLink
            content={t("profile.french")}
            to={""}
            endIcon={isFr ? "check" : undefined}
            onClick={onClick(() => setFrench())}
            data-test="profile.french"
          />
          <NavLink
            content={t("profile.english")}
            to={""}
            endIcon={!isFr ? "check" : undefined}
            onClick={onClick(() => setEnglish())}
            data-test="profile.english"
          />
        </Box>
        <Box>
          <Typography variant="h2" mb={spacing.mediumMobile}>
            {t("profile.currency_title")}
          </Typography>
          <NavLink
            content={t("general.currency.cad")}
            to={""}
            endIcon={isCAD() ? "check" : undefined}
            onClick={onClick(setCAD)}
          />
          <NavLink
            content={t("general.currency.usd")}
            to={""}
            endIcon={isUSD() ? "check" : undefined}
            onClick={onClick(setUSD)}
          />
        </Box>
      </Box>
      <RageQuitModal
        open={isRageQuitModalOpen}
        onClose={() => {
          setIsRageQuitModalOpen(false);
          toggleNavigationDrawerOpen?.();
        }}
      />
    </Box>
  );
};

export default memo(UserProfileDrawerContent);
